import { createSlice, Dispatch, PayloadAction } from "@reduxjs/toolkit";

import Agreement from "../../entities/Agreement";
import AgreementConfirmation from "../../entities/AgreementConfirmation";
import i18n from "../../i18n";
import RecordStatus from "../../util/config/recordStatus";
import { apiCallBegan, ApiCallFailedPayload } from "../api";
import { authHeader, getWorkerId } from "../users/auth";
import ResponseBase from "../types/ResponseBase";
import SliceCollectionBase from "../types/SliceCollectionBase";
import ResponseBaseInterval from "../types/ResponseBaseInterval";
import { AppDispatch, AppState } from "../configureStore";
import { getEnumValueByKeySafe, getEnumValueByKeyStrict } from "components/utils";

// RESPONSE DECLARATIONS //
interface AgreementResponse extends ResponseBaseInterval {
    AgreementConfirmations: AgreementConfirmationResponse[];
    CompanyId?: number;
    CompanyName: string;
    ConfirmationsTotal?: number;
    CreatedAt?: string;
    CreatedBy: string;
    CreatedById: number;
    Description: string;
    Link: string;
    Mandatory: boolean;
    ModifiedAt?: string;
    NotificationRequired: boolean;
    RelatedRole: string;
    SentToTotal?: number;
    Sequence: number;
    Status: keyof typeof RecordStatus;
    TargetsTotal: number;
    Title: string;
}

interface AgreementConfirmationResponse extends ResponseBase {
    AgreementId: number;
    Confirmed: boolean;
    ConfirmedAt?: string;
    NotificationAddress: string;
    NotificationResponse: string;
    NotificationSent: boolean;
    NotifiedAt?: string;
    PersonId: number;
    PersonName: string;
}

// SLICE //
interface State extends SliceCollectionBase<Agreement> {
    forWorker: SliceCollectionBase<Agreement>;
    confirmations: SliceCollectionBase<AgreementConfirmation>;
    confirmationError: null | string;
};

const initialState: State = {
    list: [],
    loading: false,
    lastFetch: null,
    error: null,

    forWorker: {
        list: [],
        loading: false,
        lastFetch: null,
        error: null,
    },

    confirmations: {
        list: [],
        loading: false,
        lastFetch: null,
        error: null
    },

    confirmationError: null
};

const slice = createSlice({
    name: "agreements",
    initialState,
    reducers: {
        //GET AGREEMENTS
        agreementsRequested: (agreements) => {
            agreements.loading = true;
            agreements.error = null;
        },
        agreementsReceived: (agreements, action: PayloadAction<AgreementResponse[]>) => {
            agreements.list = agreementListSchema(action.payload);
            agreements.loading = false;
            agreements.lastFetch = Date.now();
        },
        agreementsRequestFailed: (agreements, action: PayloadAction<ApiCallFailedPayload>) => {
            agreements.loading = false;
            agreements.error = action.payload;
        },

        //GET AGREEMENTS FOR WORKER
        agreementsForWorkerRequested: (agreements) => {
            agreements.forWorker.loading = true;
            agreements.forWorker.error = null;
        },
        agreementsForWorkerReceived: (agreements, action: PayloadAction<AgreementResponse[]>) => {
            agreements.forWorker.list = agreementListSchema(action.payload);
            agreements.forWorker.loading = false;
            agreements.forWorker.lastFetch = Date.now();
        },
        agreementsForWorkerRequestFailed: (agreements, action: PayloadAction<ApiCallFailedPayload>) => {
            agreements.forWorker.loading = false;
            agreements.forWorker.error = action.payload;
        },

        //GET USERS CONFIRMATIONS INFO
        confirmationsInfoRequested: (agreements) => {
            agreements.confirmations.list = [];
            agreements.confirmations.loading = true;
            agreements.confirmations.error = null;
        },
        confirmationsInfoReceived: (agreements, action: PayloadAction<AgreementConfirmationResponse[]>) => {
            agreements.confirmations.list = confirmationListSchema(action.payload);
            agreements.confirmations.loading = false;
            agreements.confirmations.lastFetch = Date.now();
        },
        confirmationsInfoRequestFailed: (agreements, action: PayloadAction<ApiCallFailedPayload>) => {
            agreements.confirmations.loading = false;
            agreements.confirmations.error = action.payload;
        },

        // POST AGREEMENT
        agreementPosted: (agreements) => {
            agreements.loading = true;
            agreements.error = null;
        },
        agreementPostSuccess: (agreements, action: PayloadAction<AgreementResponse>) => {
            agreements.loading = false;
            const newAgreement = agreementSchema(action.payload);
            if (newAgreement) {
                agreements.list.push(newAgreement);
            }
        },
        agreementPostFailed: (agreements, action: PayloadAction<ApiCallFailedPayload>) => {
            agreements.loading = false;
            agreements.error = action.payload;
        },

        // ARCHIVE AGREEMENT
        agreementArchivedSuccess: (agreements, action: PayloadAction<Agreement | null>) => {
            const archivedAgreementFromResponse = action.payload;
            if (archivedAgreementFromResponse) {
                const currentAgreement = agreements.list.find(a => a.Id === archivedAgreementFromResponse.Id);
                if (currentAgreement) {
                    currentAgreement.Status = RecordStatus.Archived;
                }
            }
        },

        // HIDE AGREEMENT
        agreementHideSuccess: (agreements, action) => {
            const hideAgreementFromResponse: Agreement = action.payload;
            const currentAgreement = agreements.list.find(a => a.Id === hideAgreementFromResponse.Id);
            if (currentAgreement) {
                currentAgreement.Status = hideAgreementFromResponse.Status;
            }
        },

        // DELETE AGREEMENT
        agreementDeletedSuccess: (agreements, action: PayloadAction<Agreement | null>) => {
            const deletedAgreement = action.payload;
            if (deletedAgreement) {
                agreements.list = [...agreements.list].filter(a => a.Id !== deletedAgreement.Id);
            }
        },

        // CONFIRM AGREEMENT
        agreementConfirmedSuccess: (agreements, action: PayloadAction<AgreementConfirmation | null>) => {
            const confirmation = action.payload;
            if (confirmation) {
                const currentAgreement = agreements.list.find(a => a.Id === confirmation.AgreementId);
                if (currentAgreement && currentAgreement.AgreementConfirmations) {
                    currentAgreement.AgreementConfirmations.push(confirmation);
                }

            }
            agreements.confirmationError = null;
        },
        agreementConfirmedFailed: (agreements, action) => {
            agreements.confirmationError = action.payload;
        },

        // UPDATE SEQUENCES
        agreementSequencesUpdateSuccess: (agreements, action: PayloadAction<Agreement[] | null>) => {
            const updatedAgreements = action.payload;
            if (updatedAgreements) {
                for (let updatedAgreement of updatedAgreements) {
                    const currentAgreement = agreements.list.find(a => a.Id === updatedAgreement.Id);
                    if (!currentAgreement) {
                        continue;
                    }
                    currentAgreement.Sequence = updatedAgreement.Sequence;
                }
            }
        }
    }
});

export const {
    agreementsRequested,
    agreementsReceived,
    agreementsRequestFailed,

    agreementsForWorkerRequested,
    agreementsForWorkerReceived,
    agreementsForWorkerRequestFailed,

    confirmationsInfoRequested,
    confirmationsInfoReceived,
    confirmationsInfoRequestFailed,

    agreementPosted,
    agreementPostSuccess,
    agreementPostFailed,

    agreementArchivedSuccess,

    agreementHideSuccess,

    agreementDeletedSuccess,

    agreementConfirmedSuccess,
    agreementConfirmedFailed,

    agreementSequencesUpdateSuccess
} = slice.actions;

export default slice.reducer;

export const confirmMultipleAgreements = (agreementIds: number[]) => (dispatch: AppDispatch, getState: () => AppState) => {
    if (!Array.isArray(agreementIds)) {
        return;
    }

    if (agreementIds.length <= 0) {
        return;
    }
    const workerId = getWorkerId(getState());

    const url = `/api/agreement/confirmMultiple/${workerId}`;

    dispatch(apiCallBegan({
        url,
        method: "POST",
        data: agreementIds,
        headers: {
            ...authHeader(),
            "Accept-Language": i18n.language,
            Accept: "application/json"
        },
        onSuccess: agreementConfirmedSuccess.type
    }));
};

export const getWorkerAgreements = (companyId?: number) => (dispatch: AppDispatch, getState: () => AppState) => {
    if (getState().entities.agreements.forWorker.loading) {
        return;
    }
    const workerId = getWorkerId(getState());

    const url = `/api/agreement/forworker?workerId=${workerId}` + (companyId ? `&companyId=${companyId}` : '');
    dispatch(apiCallBegan({
        url,
        method: "GET",
        headers: { ...authHeader(), "Accept-Language": i18n.language },
        onStart: agreementsForWorkerRequested.type,
        onSuccess: agreementsForWorkerReceived.type,
        onError: agreementsForWorkerRequestFailed.type
    }));
};


export const getAllAgreements = (companyId?: number) => (dispatch: AppDispatch, getState: () => AppState) => {
    if (getState().entities.agreements.loading) {
        return;
      }

    const url = "/api/agreement" + (companyId ? `?companyId=${companyId}` : '');
    dispatch(apiCallBegan({
        url,
        method: "GET",
        headers: { ...authHeader(), "Accept-Language": i18n.language },
        onStart: agreementsRequested.type,
        onSuccess: agreementsReceived.type,
        onError: agreementsRequestFailed.type
    }));
};

export const getAgreementConfirmationInfo = (agreementId: number) => (dispatch: AppDispatch, getState: () => AppState) => {
    if (getState().entities.agreements.confirmations.loading) {
        return;
      }
    
      const url = `/api/agreement/confirmations?agreementId=${agreementId}`;
      dispatch(apiCallBegan({
        url,
        method: "GET",
        headers: { ...authHeader(), "Accept-Language": i18n.language },
        onStart: confirmationsInfoRequested.type,
        onSuccess: confirmationsInfoReceived.type,
        onError: confirmationsInfoRequestFailed.type
    }));
};

export const postAgreement = (data: Agreement) => (dispatch: AppDispatch) => {
    const url = "/api/agreement";
    dispatch(apiCallBegan({
        url,
        method: data?.Id ? "PATCH" : "POST",
        data,
        headers: {
            ...authHeader(),
            "Accept-Language": i18n.language,
            Accept: "application/json",
            "Content-Type": "application/json"
        },
        onStart: agreementPosted.type,
        onSuccess: agreementPostSuccess.type,
        onError: agreementPostFailed.type
    }));
};

export const archiveAgreement = (agreementId: number) => (dispatch: AppDispatch) => {
    const url = `/api/agreement/${agreementId}/archive`;
    dispatch(apiCallBegan({
        url,
        method: "POST",
        headers: {
            ...authHeader(),
            "Accept-Language": i18n.language,
            Accept: "application/json"
        },
        onSuccess: agreementArchivedSuccess.type
    }));
};

export const toggleHideAgreement = (agreementId: number) => (dispatch: AppDispatch) => {
    const url = `/api/agreement/${agreementId}/hide`;
    dispatch(apiCallBegan({
        url,
        method: "POST",
        headers: {
            ...authHeader(),
            "Accept-Language": i18n.language,
            Accept: "application/json"
        },
        onSuccess: agreementHideSuccess.type
    }));
};

export const deleteAgreement = (agreementId: number) => (dispatch: AppDispatch) => {
    const url = `/api/agreement/${agreementId}`;
    dispatch(apiCallBegan({
        url,
        method: "DELETE",
        headers: {
            ...authHeader(),
            "Accept-Language": i18n.language,
            Accept: "application/json"
        },
        onSuccess: agreementDeletedSuccess.type
    }));
};

export const confirmAgreement = (agreementId: number) => (dispatch: AppDispatch) => {
    const url = `/api/agreement/${agreementId}/confirm`;
    dispatch(apiCallBegan({
        url,
        method: "POST",
        headers: {
            ...authHeader(),
            "Accept-Language": i18n.language,
            Accept: "application/json"
        },
        onSuccess: agreementConfirmedSuccess.type
    }));
};


export const updateAgreementsOrder = (agreements: Agreement[]) => (dispatch: AppDispatch) => {
    if (!Array.isArray(agreements)) {
        return;
    }

    if (agreements.length <= 0) {
        return;
    }

    const url = `/api/agreement/updateSequences`;

    dispatch(apiCallBegan({
        url,
        method: "POST",
        data: agreements,
        headers: {
            ...authHeader(),
            "Accept-Language": i18n.language,
            Accept: "application/json"
        },
        onSuccess: agreementSequencesUpdateSuccess.type
    }));
};

// NORMALIZATION //
const agreementSchema = (resp: AgreementResponse): Agreement | null => {
    if (!resp) {
        return null;
    }
    return ({
        AgreementConfirmations: confirmationListSchema(resp.AgreementConfirmations),
        CompanyId: resp.CompanyId,
        CompanyName: resp.CompanyName,
        ConfirmationsTotal: resp.ConfirmationsTotal,
        CreatedAt: resp.CreatedAt,
        CreatedBy: resp.CreatedBy,
        CreatedById: resp.CreatedById,
        Description: resp.Description,
        EndDate: resp.EndDate,
        Id: resp.Id,
        Link: resp.Link,
        Mandatory: resp.Mandatory,
        ModifiedAt: resp.ModifiedAt,
        NotificationRequired: resp.NotificationRequired,
        RelatedRole: resp.RelatedRole,
        SentToTotal: resp.SentToTotal,
        Sequence: resp.Sequence,
        StartDate: resp.StartDate,
        Status: getEnumValueByKeyStrict(RecordStatus, resp.Status),
        Title: resp.Title,
    })
};

const agreementListSchema = (payload: AgreementResponse[]): Agreement[] => {
    if (!payload || payload.length === 0) {
        return [];
    }
    return payload
        .filter((a) => a !== null)
        .map((resp: AgreementResponse): Agreement | null => agreementSchema(resp))
        .filter((setting): setting is Agreement => setting !== null);
};

const confirmationSchema = (payload: AgreementConfirmationResponse): AgreementConfirmation | null => {
    if (!payload) {
        return null;
    }
    return ({
        AgreementId: payload.AgreementId,
        Confirmed: payload.Confirmed,
        ConfirmedAt: payload.ConfirmedAt,
        Id: payload.Id,
        NotificationAddress: payload.NotificationAddress,
        NotificationResponse: payload.NotificationResponse,
        NotificationSent: payload.NotificationSent,
        NotifiedAt: payload.NotifiedAt,
        PersonId: payload.PersonId,
        PersonName: payload.PersonName
    })
};

const confirmationListSchema = (payload: AgreementConfirmationResponse[]): AgreementConfirmation[] => {
    if (!payload || payload.length === 0) {
        return [];
    }
    return payload
        .filter((c): c is AgreementConfirmationResponse => c !== null)
        .map((resp: AgreementConfirmationResponse): AgreementConfirmation | null => confirmationSchema(resp))
        .filter((setting): setting is AgreementConfirmation => setting !== null);
};